// src/services/UserService.js - Updated with Firebase
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';

// User tier definitions remain the same
export const USER_TIERS = {
    NONE: 'none',   // Guest
    BASIC: 'basic', // Signed up user
    PRO: 'pro'      // Previously "premium"
  };

const UserService = {
  /**
   * Get user's data from Firestore
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object|null>} User data or null
   */
  getUserData: async (userId) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error("Error getting user data:", error);
      return null;
    }
  },
  
  /**
   * Update a user's properties
   * @param {string} userId - User ID
   * @param {Object} updates - Object with properties to update
   * @returns {Promise<Object>} Updated user object
   */
  updateUser: async (userId, updates) => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, updates);
      
      // Get updated user data
      const userDoc = await getDoc(userDocRef);
      return userDoc.data();
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },
  
  /**
   * Update a user's tier
   * @param {string} userId - User ID
   * @param {string} tier - New tier (from USER_TIERS)
   * @returns {Promise<Object>} Updated user object
   */
  updateTier: async (userId, tier) => {
    if (!Object.values(USER_TIERS).includes(tier)) {
      throw new Error('Invalid tier');
    }
    
    return UserService.updateUser(userId, { tier });
  },
  
  /**
   * Save a workout for a user
   * @param {string} userId - User ID
   * @param {Object} workout - Workout to save
   * @returns {Promise<Array>} Updated saved workouts array
   */
  saveWorkout: async (userId, workout) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      const savedWorkouts = userData.savedWorkouts || [];
      
      // Create a new workout object with ID and date
      const workoutToSave = {
        ...workout,
        id: Date.now(),
        date: new Date().toISOString(),
      };
      
      // Add to user's saved workouts
      const updatedWorkouts = [...savedWorkouts, workoutToSave];
      
      await updateDoc(userDocRef, {
        savedWorkouts: updatedWorkouts
      });
      
      return updatedWorkouts;
    } catch (error) {
      console.error("Error saving workout:", error);
      throw error;
    }
  },
  
  /**
   * Delete a saved workout
   * @param {string} userId - User ID
   * @param {number} workoutId - Workout ID to delete
   * @returns {Promise<Array>} Updated saved workouts array
   */
  deleteWorkout: async (userId, workoutId) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      const savedWorkouts = userData.savedWorkouts || [];
      
      // Filter out the workout to delete
      const updatedWorkouts = savedWorkouts.filter(
        workout => workout.id !== workoutId
      );
      
      await updateDoc(userDocRef, {
        savedWorkouts: updatedWorkouts
      });
      
      return updatedWorkouts;
    } catch (error) {
      console.error("Error deleting workout:", error);
      throw error;
    }
  },
  
  /**
   * Get a user's saved workouts
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Saved workouts array
   */
  getSavedWorkouts: async (userId) => {
    try {
      const userData = await UserService.getUserData(userId);
      return userData?.savedWorkouts || [];
    } catch (error) {
      console.error("Error getting saved workouts:", error);
      throw error;
    }
  },
  
  /**
   * Add workout to history
   * @param {string} userId - User ID
   * @param {Object} workout - Completed workout
   * @returns {Promise<Array>} Updated history array
   */
  addWorkoutToHistory: async (userId, workout) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      const workoutHistory = userData.workoutHistory || [];
      
      // Add workout with timestamp
      const workoutWithTimestamp = {
        ...workout,
        completedAt: new Date().toISOString()
      };
      
      const updatedHistory = [...workoutHistory, workoutWithTimestamp];
      
      await updateDoc(userDocRef, {
        workoutHistory: updatedHistory
      });
      
      return updatedHistory;
    } catch (error) {
      console.error("Error adding workout to history:", error);
      throw error;
    }
  },
  
  /**
   * Get workout history
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Workout history
   */
  getWorkoutHistory: async (userId) => {
    try {
      const userData = await UserService.getUserData(userId);
      return userData?.workoutHistory || [];
    } catch (error) {
      console.error("Error getting workout history:", error);
      throw error;
    }
  },
  
  /**
   * Update favorite exercises
   * @param {string} userId - User ID
   * @param {Array} favoriteExercises - Array of favorite exercise IDs
   * @returns {Promise<Array>} Updated favorites
   */
  updateFavorites: async (userId, favoriteExercises) => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, { favoriteExercises });
      return favoriteExercises;
    } catch (error) {
      console.error("Error updating favorites:", error);
      throw error;
    }
  },
  
  /**
   * Get favorite exercises
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Favorite exercises
   */
  getFavorites: async (userId) => {
    try {
      const userData = await UserService.getUserData(userId);
      return userData?.favoriteExercises || [];
    } catch (error) {
      console.error("Error getting favorites:", error);
      throw error;
    }
  }
};

export default UserService;